import { useState } from "react"
import { OpenShiftListFilter } from "../../redux/types"
import { createUseStyles } from 'react-jss'
import { Breakpoints } from "../../constants/breakpoints"
import DatePicker from "../DatePicker"
import { isDateValid } from "../../util/date"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faTimes } from "@fortawesome/free-solid-svg-icons"
import { Colors } from "../../constants/colors"
import { useTranslation } from "react-i18next"
import Switch from "../Switch"

interface Props {
    initialFilterValue: OpenShiftListFilter | undefined
    onFilterChange: (filter: OpenShiftListFilter) => void
}

const ShiftListFilter = ({initialFilterValue, onFilterChange}: Props) => {
    const styles = useStyles()
    const { t } = useTranslation('common');
    const [filterValue, setFilterValue] = useState<OpenShiftListFilter>(initialFilterValue ?? {fromDate: null, toDate: null, includeDeleted: false})

    const handleDateChanged = (propName: string, date: string | null | undefined) => {
        if (!date || date.length < 1) {
            const newFilter = {...filterValue, [propName]: null}
            setFilterValue(newFilter)
            onFilterChange(newFilter)   
        }

        if (date && isDateValid(date)) {
            const newFilter = {...filterValue, [propName]: date}
            setFilterValue(newFilter)
            onFilterChange(newFilter)
        }
    }

    const handleIncludeDeletedChanged = () => {
        const newFilter = {...filterValue, includeDeleted: !filterValue.includeDeleted}
        setFilterValue(newFilter)
        onFilterChange(newFilter)
    }

    const clearFilters = () => {
        const newFilter = {fromDate: null, toDate: null, includeDeleted: false}
        setFilterValue(newFilter)
        onFilterChange(newFilter)    
    }

    return (
        <div className={styles.container}>
            <div className={styles.field}>
                <DatePicker 
                    id='fromDate'
                    label={t('fromDate')}
                    value={filterValue.fromDate ?? ''}
                    onChange={(value) => handleDateChanged('fromDate', value)}
                />   
            </div>
            <div className={styles.field}>
                <DatePicker 
                    id='toDate'
                    label={t('toDate')}
                    value={filterValue.toDate ?? ''}
                    onChange={(value) => handleDateChanged('toDate', value)}
                />   
            </div>
            <div className={styles.smallField}>
                <div style={{marginTop: 35}}>
                    <Switch 
                        id='includeDeleted'
                        key='includeDeleted'
                        label={t('includeDeleted', {ns: 'openShifts'})}
                        checked={filterValue.includeDeleted}
                        onChange={() => handleIncludeDeletedChanged()}
                    />
                </div>
            </div>
            <div className={styles.clearFiltersContainer}>
                <div className={styles.clearFiltersButton} onClick={() => clearFilters()} title={t('clearFilters')}>
                    <FontAwesomeIcon
                        size='lg'
                        icon={faTimes}
                        color={Colors.gray1}
                    />
                </div>
            </div>
        </div>
    )
}

export default ShiftListFilter

const useStyles = createUseStyles({
    container: {
        display: 'flex',
        flex: 1,
        flexWrap: 'wrap',
        flexDirection: 'row',
        alignContent: 'center',
        justifyContent: 'flex-start',
    },
    field: {
        width: '100%',    
        [`@media (min-width: ${Breakpoints.tablet}px)`]: {
            width: 280,
            marginRight: 30,
        },
    },
    smallField: {
        width: '100%',    
        [`@media (min-width: ${Breakpoints.tablet}px)`]: {
            width: 150,
            marginRight: 30,
        },
    },
    clearFiltersContainer: {
       alignSelf: 'flex-end' 
    },
    clearFiltersButton: {
        marginBottom: 12,
        '&:hover': {
            cursor: 'pointer',
        }
    }
  })